import { useState, useEffect } from 'react';
import { notification } from 'tea-component';
import { getOverviewBurnRate, getStudioBurnRate } from '@sg/services/index';
import { chartOrderedColors } from '@sg/utils/useChartOptions';
import {
  resolveActualForecastStyle,
  resolveMetricTooltipDate,
} from '../../utils/metricChartTooltip';
import {
  filterValidAxisRows,
  normalizeMetricAxisFields,
  type TMetricScope,
} from '../../utils/normalizeMetricAxisRows';

const BURN_RATE_NAME_MAP: Record<string, string> = {
  // Order = stack bottom→top and tooltip after Total (matches product design).
  rd_cost: 'R&D Cost',
  sm_expenses: 'S&M Cost',
  ga_non_rd: 'G&A - Non R&D expense',
  other_cops_excl_channel: 'Other Cost',
};

const BURN_RATE_KEYS = Object.keys(BURN_RATE_NAME_MAP);

export type TBurnRateScopeOptions =
  | { scope: 'studio'; studioId: string }
  | { scope: 'portfolio' };

export const useBurnRateData = (
  scopeOptions: TBurnRateScopeOptions,
  activeDateTypeSegment: string,
  selectedMonth?: string | null,
) => {
  const [burnRateData, setBurnRateData] = useState<any>({});
  const [burnRateTableData, setBurnRateTableData] = useState<any[]>([]);
  const [burnRateUpdateTime, setBurnRateUpdateTime] = useState<string>('');
  const [burnRateLoading, setBurnRateLoading] = useState<boolean>(false);

  const scope: TMetricScope = scopeOptions.scope;
  const studioId = scopeOptions.scope === 'studio' ? scopeOptions.studioId : '';

  useEffect(() => {
    if (scope === 'studio' && !studioId) {
      setBurnRateData({});
      setBurnRateTableData([]);
      setBurnRateUpdateTime('');
      setBurnRateLoading(false);
      return;
    }

    const fetchBurnRate = async () => {
      setBurnRateLoading(true);
      // Clear before fetch so a null datatime from the new segment does not keep the old value.
      setBurnRateUpdateTime('');
      try {
        const yearMode = activeDateTypeSegment === 'year';
        const viewMode = yearMode ? 'by_year' as const : 'by_month' as const;
        const basePayload: {
          view_mode: 'by_month' | 'by_year';
          datatime?: string;
        } = { view_mode: viewMode };
        if (selectedMonth) {
          basePayload.datatime = selectedMonth;
        }

        const res = scope === 'portfolio'
          ? await getOverviewBurnRate(basePayload)
          : await getStudioBurnRate({
            ...basePayload,
            studio_id: studioId,
          });
        const responseData = res?.data?.data || [];
        const responseDatatime = res?.data?.datatime ?? '';
        setBurnRateUpdateTime(responseDatatime);
        const burnRateRows = filterValidAxisRows(
          responseData.map((item: any) => {
            const axis = normalizeMetricAxisFields(item, scope);
            const toNumberOrNull = (value: any): number | null => {
              if (value === null || value === undefined) return null;
              const numericValue = Number(value);
              return Number.isFinite(numericValue) ? numericValue : null;
            };
            return {
              ...axis,
              // Tooltip date; empty row date falls back to response datatime.
              date: resolveMetricTooltipDate(item?.date, item?.month, responseDatatime),
              title: item?.studio_name,
              // isActual: item?.is_actual,
              source: item?.source,
              rd_cost: toNumberOrNull(item?.rd_cost),
              sm_expenses: toNumberOrNull(item?.sm_expenses),
              ga_non_rd: toNumberOrNull(item?.ga_non_rd),
              other_cops_excl_channel: toNumberOrNull(item?.other_cops_excl_channel),
            };
          }),
        );

        // Studios is a time series (sort by date); Portfolio keeps backend studio order.
        const orderedRows = scope === 'studio'
          ? [...burnRateRows].sort((a: any, b: any) => a.key.localeCompare(b.key))
          : burnRateRows;

        const tableRows = orderedRows.map((row: any) => ({
          key: row.key,
          date: row.label,
          RDCost: row.rd_cost,
          SMExpenses: row.sm_expenses,
          GANonRDExpense: row.ga_non_rd,
          OtherCopsExclChannel: row.other_cops_excl_channel,
        }));
        setBurnRateTableData(tableRows);

        const xAxisData = orderedRows.map((item: any) => item.label);
        const seriesData = BURN_RATE_KEYS.map((metricKey, index) => {
          const baseColor =
            chartOrderedColors[index] || chartOrderedColors[0] || '#7F6FF1';
          return {
            name: BURN_RATE_NAME_MAP[metricKey],
            type: 'bar',
            stack: 'BurnRate',
            barWidth: '40%',
            // Keep legend marker color consistent with tooltip/bar marker color.
            color: baseColor,
            itemStyle: {
              color: baseColor,
            },
            data: orderedRows.map((row: any) => {
              const value = row?.[metricKey];
              const { useForecastStyle, tooltipHeaderRight } = resolveActualForecastStyle({
                // isActual: row.isActual,
                source: row.source,
              });
              // Always return an object (even when value is null) so stacked tooltips
              // can still read title / date / Actual|Forecast from any series point.
              return {
                value: value === null || value === undefined ? null : value,
                isActual: !useForecastStyle,
                tooltipWidth: '240px',
                // Optional tooltip title (Portfolio maps studio_name → title).
                title: row.title,
                date: row.date,
                tooltipHeaderRight,
                // Recalculate total from currently visible legend series.
                totalFromVisible: true,
                totalTitleText: 'Total',
                itemStyle: {
                  color: baseColor,
                  opacity: useForecastStyle ? 0.3 : 1,
                },
              };
            }),
          };
        });

        setBurnRateData({
          bar: {
            xAxisData,
            type: 'bar',
            seriesData,
          },
        });
      } catch (error) {
        console.error('Error fetching burn rate data:', error);
        notification.error({
          description: 'Failed to fetch burn rate data',
        });
        setBurnRateUpdateTime('');
        setBurnRateTableData([]);
        // Empty series so Chart still mounts and shows "Data not applicable or not available".
        setBurnRateData({
          bar: {
            xAxisData: [],
            type: 'bar',
            seriesData: [],
          },
        });
      } finally {
        setBurnRateLoading(false);
      }
    };

    fetchBurnRate();
  }, [scope, studioId, activeDateTypeSegment, selectedMonth]);

  return {
    burnRateData,
    burnRateTableData,
    burnRateUpdateTime,
    burnRateLoading,
  };
};
